// Transformation d'une notation de seance (ex: "4x(6'@HP-R1'-0'50''@VMA-R2'30'')")
// vers le format texte des seances structurees d'intervals.icu, qui
// synchronise ensuite les seances planifiees vers les montres (Garmin...).
//
// Syntaxe intervals.icu utilisee :
//   - une etape par ligne, prefixee par "- " ; le texte avant la premiere
//     duree est le message affiche sur la montre (il ne doit donc contenir
//     ni chiffre ni unite, d'ou les libelles "Cinq K", "Dix K"...) ;
//   - durees "6m", "50s", "1m15s" ("m" = MINUTES) ; distances "2km",
//     "600mtr" ("mtr" = metres) ;
//   - cible d'allure absolue en plage : "4:28-4:18/km Pace" ; effort
//     progressif ("30'@HP-10K") : "ramp 4:30-4:10/km Pace", de l'allure de
//     depart a l'allure d'arrivee ;
//   - repetitions : une ligne "4x" seule, entouree de lignes vides, suivie
//     des etapes repetees. Les repetitions IMBRIQUEES ne sont pas
//     supportees par intervals.icu : la seance est donc d'abord mise a plat
//     en une suite d'etapes, puis recompressee en blocs "Nx" non imbriques
//     (voir compresser) ;
//   - appui sur lap : "- Press lap <message> <duree>" ; l'etape attend
//     l'appui sur le bouton lap de la montre (etape ouverte), la duree
//     n'etant qu'une estimation pour le planning (une etape sans duree est
//     ignoree par intervals.icu).
//
// Conventions :
//   - une recuperation ecrite dans un bloc est jouee apres CHAQUE repetition
//     (y compris la derniere, qui sert de transition vers le bloc suivant) ;
//   - deux recuperations consecutives (ex: fin de serie "5x(...-R1'15'')"
//     suivie de la recup de serie "-R3'") n'en font qu'une : la derniere ;
//   - une condition d'execution ("1'@5k[côte]") prefixe le message
//     ("Côte Cinq K") ; en cote, descente ou trail, l'etape n'a pas de
//     cible d'allure (cf Condition.sansAllure) ;
//   - une allure cible est une plage allant de l'allure indiquee a l'allure
//     de la zone immediatement plus lente des parametres d'allures (ordre
//     des % VMA) : "5'@5K" se court entre l'allure 5K et l'allure 10K,
//     "@MP" entre Marathon et Steady, "@EASY" entre Easy et Slow (RECUP) ;
//   - les recuperations et les efforts @RECUP ont toujours une cible :
//     plus lent que l'allure de la zone Recuperation (RECUP), sans limite
//     basse (borne lente ecrite ALLURE_SANS_LIMITE, intervals.icu
//     n'acceptant pas de plage ouverte).
//
//   - dans le corps de seance, chaque bloc de repetitions est precede d'un
//     appui sur lap (ETAPE_APPUI_LAP) : l'athlete lance lui-meme le bloc ;
//     apres un echauffement, le corps de seance commence aussi par un appui
//     sur lap (mise en condition : cote, changement de chaussures...), un
//     seul si le corps commence par un bloc de repetitions.
//
// La notation recue doit deja integrer l'ajustement de recuperation de
// l'utilisateur (ajusterNotation) : ce module reste pur (sans React).

import { type ZoneRow } from './allureTable.ts';
import {
  analyserSeance,
  type CodeCondition,
  type Condition,
  CONDITIONS,
  RE_CONDITION,
  trouverCondition,
  diviserEnRespectantParentheses,
  lireAllure,
  normaliserGuillemets,
  parseRecuperation,
  remplacerPlusInternes,
  parseValeurEffort,
  recollerAlluresProgressives,
  resoudreCodeCanonique,
  trouverZone,
} from './seanceEngine.ts';

export type EtapeIcu =
  // codeFin : allure d'arrivee d'un effort progressif ("30'@HP-10K").
  // condition : condition d'execution de l'effort ("1'@5k[côte]").
  | { type: 'effort'; code: string; codeFin?: string; condition?: CodeCondition; secondes?: number; distanceM?: number }
  | { type: 'recup'; secondes: number; distanceM?: number };

export type OptionsIcu = {
  /** Echauffement ajoute en tete (minutes, allure Endurance). 0 = aucun. */
  echauffementMin?: number;
  /** Retour au calme ajoute en fin (minutes, allure Endurance). 0 = aucun. */
  retourCalmeMin?: number;
  /**
   * Notation de l'echauffement du modele de seance (prioritaire sur
   * echauffementMin), ecrite dans la section "Warmup".
   */
  echauffement?: string;
  /** Notation de la recuperation du modele (prioritaire sur retourCalmeMin), section "Cooldown". */
  retourCalme?: string;
};

export type ResultatIcu =
  | { texte: string; etapes: EtapeIcu[]; erreur?: undefined }
  | { erreur: string; texte?: undefined; etapes?: undefined };

// Messages affiches sur la montre, sans chiffre (cf. en-tete).
const LIBELLES: Record<string, string> = {
  VMA: 'VMA',
  '3K': 'Trois K',
  '5K': 'Cinq K',
  '10K': 'Dix K',
  HP: 'Semi',
  MP: 'Marathon',
  STEADY: 'Soutenu',
  EASY: 'Facile',
  RECUP: 'Récup',
};

const RE_REPS = /^(\d+)\s*[xX×]\s*/;

// Etape d'attente avant un bloc de repetitions : message sans chiffre (cf.
// en-tete), duree courte qui ne compte que pour l'estimation du planning.
const ETAPE_APPUI_LAP = '- Press lap Appuyer pour passer au bloc suivant 15s';

/* =================================================================
   MISE A PLAT DE LA NOTATION
   ================================================================= */

function fermeALaFin(t: string): boolean {
  let profondeur = 0;
  for (let i = 0; i < t.length; i += 1) {
    if (t[i] === '(') profondeur += 1;
    if (t[i] === ')') {
      profondeur -= 1;
      if (profondeur === 0) return i === t.length - 1;
    }
  }
  return false;
}

function retirerParenthesesEnglobantes(texte: string): string {
  let t = texte.trim();
  while (t.startsWith('(') && fermeALaFin(t)) t = t.slice(1, -1).trim();
  return t;
}

/** "Nx(...)", "NxMx(...)" ou "(...)" : contenu repete, mis a plat. */
function aplatirUnite(texte: string, vma: number, table: ZoneRow[]): EtapeIcu[] {
  let t = texte.trim();
  let reps = 1;
  let m = t.match(RE_REPS);
  while (m) {
    reps *= parseInt(m[1], 10);
    t = t.slice(m[0].length).trim();
    m = t.match(RE_REPS);
  }
  const sequence = aplatirSequence(retirerParenthesesEnglobantes(t), vma, table);
  const etapes: EtapeIcu[] = [];
  for (let i = 0; i < reps; i += 1) etapes.push(...sequence);
  return etapes;
}

function recuperation(texte: string, vma: number, table: ZoneRow[]): EtapeIcu {
  const r = parseRecuperation(texte, vma, table);
  if (!r) throw new Error(`Récupération non reconnue : ${texte}`);
  return { type: 'recup', secondes: r.secondes, distanceM: r.distanceM };
}

/** Suite "effort-recup-effort-..." (separateur "-" hors parentheses). */
function aplatirSequence(texte: string, vma: number, table: ZoneRow[]): EtapeIcu[] {
  const etapes: EtapeIcu[] = [];
  for (const brut of recollerAlluresProgressives(diviserEnRespectantParentheses(texte, '-'))) {
    const unite = brut.trim();
    if (!unite) continue;
    if (RE_REPS.test(unite) || unite.startsWith('(')) {
      etapes.push(...aplatirUnite(unite, vma, table));
      continue;
    }
    // Condition d'execution de l'effort, retiree avant la lecture.
    const etiquette = /\[([^\]]*)\]/.exec(unite);
    const condition = etiquette ? trouverCondition(etiquette[1])?.code : undefined;
    const segment = unite.replace(RE_CONDITION, '').trim();
    const arobase = segment.indexOf('@');
    if (arobase === -1) {
      etapes.push(recuperation(segment, vma, table));
      continue;
    }
    const effort = parseValeurEffort(segment.slice(0, arobase));
    const apres = segment.slice(arobase + 1).trim();
    const allure = lireAllure(apres);
    if (!effort || !allure) throw new Error(`Effort non reconnu : ${segment}`);
    const code = resoudreCodeCanonique(allure.code);
    if (!trouverZone(table, code)) throw new Error(`Allure non reconnue : ${allure.code}`);
    const codeFin = allure.codeFin ? resoudreCodeCanonique(allure.codeFin) : undefined;
    if (codeFin && !trouverZone(table, codeFin)) throw new Error(`Allure non reconnue : ${allure.codeFin}`);
    const details = {
      ...(codeFin && codeFin !== code ? { codeFin } : {}),
      ...(condition ? { condition } : {}),
    };
    etapes.push(
      effort.unite === 'temps'
        ? { type: 'effort', code, ...details, secondes: effort.secondes }
        : { type: 'effort', code, ...details, distanceM: effort.unite === 'km' ? effort.val * 1000 : effort.val },
    );
    // Recuperation collee a l'effort sans "-" (ex: "8'@MP R3'").
    const reste = allure.reste;
    if (reste) etapes.push(recuperation(reste, vma, table));
  }
  return etapes;
}

/** Deux recuperations consecutives : seule la derniere est conservee. */
function fusionnerRecuperations(etapes: EtapeIcu[]): EtapeIcu[] {
  const res: EtapeIcu[] = [];
  for (const e of etapes) {
    if (e.type === 'recup' && res.length > 0 && res[res.length - 1].type === 'recup') {
      res[res.length - 1] = e;
    } else {
      res.push(e);
    }
  }
  return res;
}

/** Suite d'etapes elementaires de la seance (sans repetition). */
export function etapesSeance(notation: string, vma: number, table: ZoneRow[]): EtapeIcu[] {
  const texte = remplacerPlusInternes(normaliserGuillemets(notation));
  const etapes: EtapeIcu[] = [];
  for (const bloc of diviserEnRespectantParentheses(texte, '+')) {
    if (bloc.trim()) etapes.push(...aplatirUnite(bloc, vma, table));
  }
  return fusionnerRecuperations(etapes);
}

/* =================================================================
   RECOMPRESSION EN BLOCS "Nx" (NON IMBRIQUES)
   ================================================================= */

type Groupe = { reps: number; etapes: EtapeIcu[] };

function cle(e: EtapeIcu): string {
  const arrondi = (v?: number) => (v === undefined ? '' : String(Math.round(v)));
  return e.type === 'effort'
    ? `E|${e.code}|${e.codeFin ?? ''}|${e.condition ?? ''}|${arrondi(e.secondes)}|${arrondi(e.distanceM)}`
    : `R|${arrondi(e.secondes)}|${arrondi(e.distanceM)}`;
}

/**
 * Decoupe optimale en groupes : minimise le nombre de lignes de la seance
 * (une ligne par etape, plus une par bloc "Nx"). A cout egal, on evite les
 * blocs qui commencent par une recuperation ("2x(R3'-6'@HP)" plutot ecrit
 * "2x(6'@HP-R3')"), puis on prefere le groupe qui couvre le plus d'etapes
 * (ex: "8x" plutot que 8 lignes).
 */
export function compresser(etapes: EtapeIcu[]): Groupe[] {
  const n = etapes.length;
  const cles = etapes.map(cle);
  const cout: number[] = new Array(n + 1).fill(0);
  const choix: { periode: number; reps: number }[] = new Array(n + 1);
  for (let i = n - 1; i >= 0; i -= 1) {
    // Cout en centiemes de ligne : +1 par bloc commencant par une recup.
    const penalite = etapes[i].type === 'recup' ? 1 : 0;
    let meilleur = 100 + cout[i + 1];
    let meilleurChoix = { periode: 1, reps: 1 };
    for (let p = 1; i + 2 * p <= n; p += 1) {
      let reps = 1;
      while (i + (reps + 1) * p <= n) {
        let identique = true;
        for (let k = 0; k < p && identique; k += 1) {
          identique = cles[i + k] === cles[i + reps * p + k];
        }
        if (!identique) break;
        reps += 1;
      }
      for (let r = 2; r <= reps; r += 1) {
        const c = (p + 1) * 100 + penalite + cout[i + p * r];
        const couverture = p * r;
        if (c < meilleur || (c === meilleur && couverture > meilleurChoix.periode * meilleurChoix.reps)) {
          meilleur = c;
          meilleurChoix = { periode: p, reps: r };
        }
      }
    }
    cout[i] = meilleur;
    choix[i] = meilleurChoix;
  }
  const groupes: Groupe[] = [];
  for (let i = 0; i < n; ) {
    const { periode, reps } = choix[i];
    groupes.push({ reps, etapes: etapes.slice(i, i + periode) });
    i += periode * reps;
  }
  return groupes;
}

/* =================================================================
   ECRITURE AU FORMAT INTERVALS.ICU
   ================================================================= */

export function formatDureeIcu(secondes: number): string {
  const total = Math.round(secondes);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  return s === 0 ? `${m}m` : `${m}m${s}s`;
}

export function formatDistanceIcu(metres: number): string {
  const m = Math.round(metres);
  return m % 1000 === 0 ? `${m / 1000}km` : `${m}mtr`;
}

function formatAllure(secondesParKm: number): string {
  const total = Math.round(secondesParKm);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Allure de la zone en s/km pour la VMA donnee (km/h). */
function allureZone(code: string, vma: number, table: ZoneRow[]): number | null {
  const zone = trouverZone(table, code);
  const pct = zone ? Number(zone.pctVMA) : NaN;
  if (!zone || !isFinite(pct) || pct <= 0 || !(vma > 0)) return null;
  return 3600 / (vma * pct);
}

/** Borne lente des plages sans limite basse (RECUP) : 20'00''/km. */
const ALLURE_SANS_LIMITE = 20 * 60;

/** Zone de la table immediatement plus lente (plus petit % VMA inferieur), ou null. */
function zonePlusLente(code: string, table: ZoneRow[]): ZoneRow | null {
  const zone = trouverZone(table, code);
  const pct = zone ? Number(zone.pctVMA) : NaN;
  if (!isFinite(pct)) return null;
  let suivante: ZoneRow | null = null;
  for (const row of table) {
    const p = Number(row.pctVMA);
    if (isFinite(p) && p > 0 && p < pct && (suivante === null || p > Number(suivante.pctVMA))) suivante = row;
  }
  return suivante;
}

/**
 * Plage cible : de l'allure de la zone (borne rapide) a celle de la zone
 * immediatement plus lente (borne lente). RECUP, la plus lente, n'a pas
 * de limite basse.
 */
function cibleAllure(code: string, vma: number, table: ZoneRow[]): string {
  const allure = allureZone(code, vma, table);
  if (allure === null) return '';
  const suivante = resoudreCodeCanonique(code) === 'RECUP' ? null : zonePlusLente(code, table);
  const lente = suivante ? allureZone(suivante.code, vma, table) : null;
  // Plage ecrite de la plus lente a la plus rapide.
  return ` ${formatAllure(Math.max(lente ?? ALLURE_SANS_LIMITE, allure))}-${formatAllure(allure)}/km Pace`;
}

function ligneEtape(e: EtapeIcu, vma: number, table: ZoneRow[]): string {
  const volume =
    e.distanceM !== undefined ? formatDistanceIcu(e.distanceM) : formatDureeIcu(e.secondes ?? 0);
  const condition = e.type === 'effort' ? CONDITIONS.find((c: Condition) => c.code === e.condition) : undefined;
  const prefixe = condition ? `${condition.libelle} ` : '';
  if (e.type === 'recup' || e.code === 'RECUP') {
    const cible = condition?.sansAllure ? '' : cibleAllure('RECUP', vma, table);
    return `- ${prefixe}${LIBELLES.RECUP} ${volume}${cible}`;
  }
  const libelle = prefixe + (LIBELLES[e.code] ?? 'Effort');
  // En cote, descente ou trail : l'allure n'est qu'un repere d'intensite, pas de cible.
  if (condition?.sansAllure) {
    return `- ${libelle}${e.codeFin ? ` à ${LIBELLES[e.codeFin] ?? 'Effort'}` : ''} ${volume}`;
  }
  if (e.codeFin) {
    // Effort progressif : rampe de l'allure de depart a celle d'arrivee.
    const debut = allureZone(e.code, vma, table);
    const fin = allureZone(e.codeFin, vma, table);
    const rampe = debut !== null && fin !== null ? ` ramp ${formatAllure(debut)}-${formatAllure(fin)}/km Pace` : '';
    return `- ${libelle} à ${LIBELLES[e.codeFin] ?? 'Effort'} ${volume}${rampe}`;
  }
  return `- ${libelle} ${volume}${cibleAllure(e.code, vma, table)}`;
}

/** Etapes d'une partie du modele (echauffement / recuperation), validee comme une seance. */
function partieModele(notation: string | undefined, libelle: string, vma: number, table: ZoneRow[]): EtapeIcu[] {
  if (!notation || !notation.trim()) return [];
  const analyse = analyserSeance(notation, vma, table);
  if (analyse.synthese.erreur) throw new Error(`${libelle} du modèle : ${analyse.synthese.erreur}`);
  return etapesSeance(notation, vma, table);
}

/**
 * Convertit une notation de seance en texte de seance intervals.icu.
 *
 * @param notation notation deja ajustee (ajusterNotation) si besoin
 * @param vma VMA de l'utilisateur (km/h), pour les allures cibles
 * @param table parametres d'allures de l'utilisateur
 */
export function versIntervalsIcu(
  notation: string,
  vma: number,
  table: ZoneRow[],
  options: OptionsIcu = {},
): ResultatIcu {
  if (!notation || !notation.trim()) return { erreur: 'Séance vide' };
  if (!(vma > 0)) return { erreur: 'VMA inconnue' };

  // Meme validation que l'editeur : une notation refusee par le moteur
  // n'est pas convertie.
  const analyse = analyserSeance(notation, vma, table);
  if (analyse.synthese.erreur) return { erreur: analyse.synthese.erreur };

  const echauffementMin = options.echauffementMin ?? 0;
  const retourCalmeMin = options.retourCalmeMin ?? 0;

  let etapes: EtapeIcu[];
  let etapesEchauffement: EtapeIcu[] = [];
  let etapesRetour: EtapeIcu[] = [];
  try {
    etapes = etapesSeance(notation, vma, table);
    etapesEchauffement = partieModele(options.echauffement, 'Échauffement', vma, table);
    etapesRetour = partieModele(options.retourCalme, 'Récupération', vma, table);
  } catch (e) {
    return { erreur: (e as Error).message };
  }
  if (etapes.length === 0) return { erreur: 'Séance vide' };

  // appuiLap : appui sur lap avant chaque bloc de repetitions (corps de seance).
  // appuiDebut : appui sur lap en tete, fusionne avec celui d'un premier bloc.
  const ecrire = (liste: EtapeIcu[], appuiLap = false, appuiDebut = false): string[] => {
    const res: string[] = [];
    compresser(liste).forEach((groupe, i) => {
      const contenu = groupe.etapes.map((e) => ligneEtape(e, vma, table));
      if ((appuiLap && groupe.reps > 1) || (appuiDebut && i === 0)) res.push(ETAPE_APPUI_LAP);
      if (groupe.reps > 1) {
        res.push('', `${groupe.reps}x`, ...contenu, '');
      } else {
        res.push(...contenu);
      }
    });
    return res;
  };

  const lignes: string[] = [];
  if (etapesEchauffement.length > 0) {
    lignes.push('Warmup', ...ecrire(etapesEchauffement), '');
  } else if (echauffementMin > 0) {
    lignes.push('Warmup', `- Échauffement ${formatDureeIcu(echauffementMin * 60)}${cibleAllure('EASY', vma, table)}`, '');
  }
  // Avec un echauffement, le corps de seance a sa propre section.
  const avecEchauffement = lignes.length > 0;
  if (avecEchauffement) lignes.push('Main Set');
  lignes.push(...ecrire(etapes, true, avecEchauffement));
  if (etapesRetour.length > 0) {
    lignes.push('', 'Cooldown', ...ecrire(etapesRetour));
  } else if (retourCalmeMin > 0) {
    lignes.push('', 'Cooldown', `- Retour au calme ${formatDureeIcu(retourCalmeMin * 60)}${cibleAllure('EASY', vma, table)}`);
  }

  // Une seule ligne vide entre les blocs, aucune en tete ni en fin.
  const texte = lignes
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    // Corps commencant par une repetition : en-tete "Main Set 4x".
    .replace(/^Main Set\n\n(\d+x)$/m, 'Main Set $1')
    .trim();
  return { texte, etapes };
}
